import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useFetchMemberIncomeReportQuery } from '../../api';
import DataTable, { SortDir } from '../../components/dataTable';
import Pagination from '../../components/pagination';
import { formatAmount } from '../../utils';

const PAGE_SIZE = 20;

const StatCard = ({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <h2 className="text-2xl font-bold text-gray-900 mt-1">{value}</h2>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const tableHeaders = [
  { label: 'Member', value: 'memberName', sortable: true },
  { label: 'Company', value: 'companyName', sortable: true },
  { label: 'Status', value: 'statusBadge', sortable: true },
  { label: 'Plan', value: 'membershipPlanName', sortable: true },
  { label: '# Payments', value: 'paymentCount', sortable: true },
  { label: 'Total Paid', value: 'totalAmountFormatted', sortable: true },
  { label: 'Last Payment', value: 'lastPaymentFormatted', sortable: true },
];

const MembersReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<string | null>('totalAmount');
  const [sortDir, setSortDir] = useState<SortDir | null>('desc');

  const sortByForBackend = useMemo(() => {
    if (!sortBy) return undefined;
    if (sortBy === 'memberName') return 'firstName';
    if (sortBy === 'statusBadge') return 'membershipStatus';
    if (sortBy === 'totalAmountFormatted') return 'totalAmount';
    if (sortBy === 'lastPaymentFormatted') return 'lastPaymentDate';
    return sortBy;
  }, [sortBy]);

  const args = useMemo(() => ({
    startDate: (startDate ?? dayjs().startOf('month')).format('YYYY-MM-DD'),
    endDate: (endDate ?? dayjs()).format('YYYY-MM-DD'),
    status,
    sortBy: sortByForBackend,
    sortDir: sortDir ?? undefined,
    page,
    size: PAGE_SIZE,
  }), [startDate, endDate, status, sortByForBackend, sortDir, page]);

  const { data, isLoading, isFetching } = useFetchMemberIncomeReportQuery(args);

  const rows = (data?.rows ?? []).map((r: any) => ({
    id: r.userId,
    memberName: [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || r.userId,
    companyName: r.companyName || '-',
    statusBadge: (() => {
      const isActive = r.membershipStatus === 'ACTIVE';
      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
            isActive ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      );
    })(),
    membershipPlanName: r.membershipPlanName || '-',
    paymentCount: r.paymentCount,
    totalAmountFormatted: (
      <span className="font-semibold text-gray-900">RWF {formatAmount(Number(r.totalAmount || 0))}</span>
    ),
    lastPaymentFormatted: r.lastPaymentDate ? moment(r.lastPaymentDate).format('DD MMM YYYY') : '-',
  }));

  const rangeLabel = `${(startDate ?? dayjs().startOf('month')).format('MMM D, YYYY')} – ${(endDate ?? dayjs()).format('MMM D, YYYY')}`;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-wrap items-center gap-3">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker label="From" value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label="To" value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }} slotProps={{ textField: { size: 'small' } }} />
        </LocalizationProvider>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden ml-auto">
          {(['ALL', 'ACTIVE', 'INACTIVE'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatus(s); setPage(0); }}
              className={`px-3 py-1.5 text-xs font-semibold border-l first:border-l-0 border-gray-200 ${
                status === s ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s === 'ALL' ? 'All' : s === 'ACTIVE' ? 'Active' : 'Inactive'}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Scoped to <span className="font-semibold text-gray-700">{rangeLabel}</span>
        {isFetching && ' · refreshing…'}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Income (all members)"
          value={`RWF ${formatAmount(Number(data?.totalIncome ?? 0))}`}
          subtitle="Sum of paid payments in range, unfiltered"
        />
        <StatCard label="Paying Members" value={String(data?.payingMembers ?? 0)} subtitle="Distinct members who paid" />
        <StatCard label="Payment Count" value={String(data?.totalPayments ?? 0)} subtitle="Total number of payment rows" />
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          headers={tableHeaders}
          data={rows}
          isLoading={isLoading || isFetching}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(0);
          }}
        />
      </div>

      <Pagination
        page={page + 1}
        onPageChange={(type: string) => {
          if (type === 'next') {
            if (page < totalPages - 1) setPage(page + 1);
          } else {
            if (page > 0) setPage(page - 1);
          }
        }}
      />
    </div>
  );
};

export default MembersReport;
